import { useState } from 'react'
import { authLogin, authParticipant, authGoogle } from '../utils/api'
import { setAuth } from '../utils/auth'
import GoogleSignInButton from './GoogleSignInButton'

const C = {
  bg:        '#05050d',
  surface:   'rgba(255,255,255,0.028)',
  surface2:  'rgba(255,255,255,0.04)',
  border:    'rgba(180,185,230,0.09)',
  borderHi:  'rgba(180,185,230,0.2)',
  accent:    '#b8bdd4',
  accentDim: 'rgba(180,185,230,0.12)',
  text:      '#dce0f0',
  textMuted: '#8890aa',
  green:     '#4dc88a',
  red:       '#d45a5a',
}
const font = {
  heading: "'Cormorant Garamond', serif",
  mono:    "'Space Mono', monospace",
  body:    "'Inter', sans-serif",
}

export default function LoginModal({ onLogin, onParticipantLogin, onClose }) {
  const [mode, setMode]       = useState('owner')      // 'owner' | 'participant'
  const [authTab, setAuthTab] = useState('google')     // 'google' | 'password'
  const [password, setPassword] = useState('')
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState(null)

  function switchMode(m) {
    setMode(m); setError(null); setPassword(''); setAuthTab('google')
  }
  function switchTab(t) {
    setAuthTab(t); setError(null); setPassword('')
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault()
    setLoading(true); setError(null)
    try {
      const data = mode === 'owner'
        ? await authLogin(password)
        : await authParticipant(password)
      if (data.ok && data.access_token) {
        setAuth(data)
        if (data.role === 'owner' || data.is_owner) onLogin?.()
        else onParticipantLogin?.(data)
      } else {
        setError(data.error || 'Неверный пароль')
        setPassword('')
      }
    } catch {
      setError('Ошибка соединения с сервером')
    } finally {
      setLoading(false)
    }
  }

  async function handleGoogle(idToken) {
    setLoading(true); setError(null)
    try {
      const data = await authGoogle(idToken)
      if (data.ok && data.access_token) {
        setAuth(data)
        if (data.role === 'owner' || data.is_owner) onLogin?.()
        else onParticipantLogin?.(data)
      } else {
        setError(data.error || 'Аккаунт с этим Google не найден. Зайди по ссылке-приглашению.')
      }
    } catch {
      setError('Ошибка соединения с сервером')
    } finally {
      setLoading(false)
    }
  }

  const inp = {
    width: '100%', boxSizing: 'border-box',
    background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 8, padding: '10px 14px',
    color: C.text, fontFamily: font.body, fontSize: 14,
    outline: 'none', marginBottom: 8,
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'rgba(10,10,22,0.97)', border: `1px solid ${C.border}`,
          borderRadius: 16, padding: '28px 32px', width: 340,
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        {/* Владелец / Участник */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 22, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}` }}>
          {[
            { id: 'owner',       label: 'Владелец' },
            { id: 'participant', label: 'Участник' },
          ].map(m => (
            <button key={m.id} onClick={() => switchMode(m.id)}
              style={{
                flex: 1, padding: '8px 0', cursor: 'pointer',
                fontFamily: font.body, fontSize: 13,
                background: mode === m.id ? C.accentDim : 'transparent',
                border: 'none',
                borderRight: m.id === 'owner' ? `1px solid ${C.border}` : 'none',
                color: mode === m.id ? C.accent : C.textMuted,
              }}>
              {m.label}
            </button>
          ))}
        </div>

        <h2 style={{ fontFamily: font.heading, fontSize: 22, color: C.accent, margin: '0 0 18px', textAlign: 'center' }}>
          {mode === 'owner' ? 'Войти как владелец' : 'Войти как участник'}
        </h2>

        {/* Google / Пароль вкладки */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
          {[
            { id: 'google',   label: 'Google' },
            { id: 'password', label: 'Пароль' },
          ].map(t => (
            <button key={t.id} onClick={() => switchTab(t.id)}
              style={{
                flex: 1, padding: '6px 0', borderRadius: 6, cursor: 'pointer',
                fontFamily: font.body, fontSize: 12,
                background: authTab === t.id ? C.accentDim : 'transparent',
                border: `1px solid ${authTab === t.id ? C.borderHi : C.border}`,
                color: authTab === t.id ? C.accent : C.textMuted,
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {authTab === 'google' && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <GoogleSignInButton onCredential={handleGoogle} disabled={loading} />
            </div>
            {mode === 'participant' && (
              <p style={{ fontFamily: font.body, fontSize: 11, color: C.textMuted, textAlign: 'center', margin: '0 0 8px', lineHeight: 1.5 }}>
                Войти можно только если Google-аккаунт уже привязан через ссылку-приглашение.
              </p>
            )}
          </div>
        )}

        {authTab === 'password' && (
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Пароль"
              autoFocus
              style={{ ...inp, borderColor: error ? C.red : C.border }}
            />
            <button
              type="submit"
              disabled={loading || !password}
              style={{
                width: '100%', padding: '10px 0', borderRadius: 8,
                cursor: loading || !password ? 'default' : 'pointer',
                fontFamily: font.body, fontSize: 14, fontWeight: 500,
                background: C.accentDim, border: `1px solid ${C.accent}`,
                color: C.accent, opacity: loading || !password ? 0.5 : 1,
                marginBottom: 0,
              }}
            >
              {loading ? 'Проверяю...' : 'Войти'}
            </button>
          </form>
        )}

        {error && (
          <p style={{ fontFamily: font.body, fontSize: 12, color: C.red, margin: '10px 0 0', textAlign: 'center' }}>
            {error}
          </p>
        )}

        <button type="button" onClick={onClose}
          style={{
            width: '100%', padding: '8px 0', borderRadius: 8, cursor: 'pointer',
            fontFamily: font.body, fontSize: 13, marginTop: 12,
            background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted,
          }}
        >
          Отмена
        </button>
      </div>
    </div>
  )
}
